import type { IngredientExtractOutput, OutgoingMealResult, OutgoingSearchResult } from "../models";
import { previewGrams } from "./food";

export interface PreviewTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  grams: number;
  /** False when no item carried a `nutritionPreview`. */
  hasNutrition: boolean;
}

export interface MealComponent<T> {
  key: string;
  name: string;
  /** True for the catch-all bucket of items without a component. */
  isOther: boolean;
  items: { item: T; index: number }[];
  totals: PreviewTotals;
}

export interface MealGroup<T> {
  key: string;
  name: string;
  /** "Recipe" | "Mixed meal" | "Supplement" | a scan type | "Meal". */
  kind: string;
  components: MealComponent<T>[];
  totals: PreviewTotals;
}

type MealItem = Partial<Pick<IngredientExtractOutput, "mealName" | "componentName" | "childOf" | "isRecipe" | "isSeveralFoodsCombined" | "isSupplement" | "scanType" | "weightGrams">> &
  Pick<OutgoingSearchResult, "nutritionPreview">;

const norm = (s?: string) => (s ?? "").replace(/\s+/g, " ").trim();
const MINOR_WORDS = new Set(["a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "with"]);

/** "cheeseburger and fries" -> "Cheeseburger and Fries". */
export const titleCase = (s: string) =>
  s.replace(/\p{L}[\p{L}'’]*/gu, (w, offset: number) => (offset > 0 && MINOR_WORDS.has(w.toLowerCase()) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)));

/** Best display name for an extracted or searched item. */
export function itemName(it: Partial<IngredientExtractOutput>): string {
  return norm(it.displayName) || norm(it.ingredientName) || norm(it.shortName) || norm(it.longName) || "Unnamed food";
}

const fmtQty = (q: unknown) => (typeof q === "number" && Number.isFinite(q) && q > 0 ? String(+q.toFixed(2)) : "");
const amountText = (q: unknown, unit: string | undefined, grams: number) => {
  const head = [fmtQty(q), norm(unit)].filter(Boolean).join(" ");
  const g = grams > 0 ? `${+grams.toFixed(grams < 10 ? 1 : 0)} g` : "";
  return head && g && !/^(g|gram|grams)$/i.test(norm(unit)) ? `${head} · ${g}` : head || g;
};

/**
 * "6 medium slice · 120 g" style amount. Uses the portion the food was linked with
 * (`nutritionPreview.portion`, which its preview macros describe and the food's portion list
 * contains); the AI's suggested `portionSize` / `portionQuantity` are only a fallback.
 */
export function itemAmount(it: Partial<IngredientExtractOutput> & Pick<OutgoingSearchResult, "nutritionPreview">): string {
  const portion = it.nutritionPreview?.portion;
  if (portion?.name) return amountText(portion.quantity, portion.name, previewGrams(it.nutritionPreview));
  return amountText(it.portionQuantity, it.portionSize, Number(it.weightGrams) || 0);
}

export function sumPreview(items: MealItem[]): PreviewTotals {
  return items.reduce<PreviewTotals>(
    (a, it) => {
      const p = it.nutritionPreview;
      if (p) {
        a.hasNutrition = true;
        a.calories += Number(p.calories) || 0;
        a.protein += Number(p.protein) || 0;
        a.carbs += Number(p.carbs) || 0;
        a.fat += Number(p.fat) || 0;
      }
      a.grams += previewGrams(it.nutritionPreview) || Number(it.weightGrams) || 0;
      return a;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0, grams: 0, hasNutrition: false },
  );
}

/** True when items carry meal / component names worth grouping by. */
export function hasMealStructure(items: MealItem[]): boolean {
  return items.some((it) => norm(it.mealName) || norm(it.componentName) || norm(it.childOf));
}

/**
 * Group flat extraction results into meals and components (`mealName` > `componentName`/`childOf`),
 * mirroring GI Coach. Items without names fall into a single "Your foods" meal / "Other items".
 */
export function groupMeals<T extends MealItem>(items: T[], opts: { fallbackMealName?: string } = {}): MealGroup<T>[] {
  const meals = new Map<string, { name: string; comps: Map<string, { name: string; isOther: boolean; items: { item: T; index: number }[] }> }>();
  items.forEach((item, index) => {
    const mName = norm(item.mealName);
    const mKey = mName.toLowerCase() || "__default";
    if (!meals.has(mKey)) meals.set(mKey, { name: mName ? titleCase(mName) : opts.fallbackMealName ?? "Your foods", comps: new Map() });
    const comps = meals.get(mKey)!.comps;
    const cName = norm(item.componentName) || norm(item.childOf);
    const cKey = cName.toLowerCase() || "__other";
    if (!comps.has(cKey)) comps.set(cKey, { name: cName ? titleCase(cName) : "Other items", isOther: !cName, items: [] });
    comps.get(cKey)!.items.push({ item, index });
  });

  return [...meals.entries()].map(([key, m]) => {
    const components = [...m.comps.entries()].map(([ckey, c]) => ({
      key: ckey,
      name: c.name,
      isOther: c.isOther,
      items: c.items,
      totals: sumPreview(c.items.map((x) => x.item)),
    }));
    const all = components.flatMap((c) => c.items.map((x) => x.item));
    const realComponents = components.filter((c) => !c.isOther).length;
    const kind = all.some((x) => x.isRecipe)
      ? "Recipe"
      : realComponents > 1 || all.some((x) => x.isSeveralFoodsCombined)
        ? "Mixed meal"
        : all.some((x) => x.isSupplement)
          ? "Supplement"
          : all[0]?.scanType
            ? titleCase(String(all[0].scanType).replace(/_/g, " "))
            : "Meal";
    return { key, name: m.name, kind, components, totals: sumPreview(all) };
  });
}

/** `extractFoodAndIngredients` result as flat items carrying `mealName` / `componentName`, ready for `groupMeals`. */
export function flattenMealResult(meal: OutgoingMealResult | null | undefined): IngredientExtractOutput[] {
  if (!meal) return [];
  return (meal.components ?? []).flatMap((c) =>
    (c.ingredients ?? []).map((ing) => ({ ...ing, mealName: meal.mealName, componentName: c.componentName })),
  );
}
